/**
 * Pitch tracking — shared by the offline reference extractor and the live scorer.
 *
 * This file is `.mjs` on purpose. `scripts/extract-contour.mjs` runs it in Node to
 * build each song's reference contour, and the browser runs it against the singer's
 * mic. Both paths MUST use the same sample rate, frame size, hop and YIN settings,
 * or the two contours are not comparable and every score is quietly wrong. One file,
 * imported with an explicit extension, is the cheapest way to guarantee that.
 * (`demo/gs` has no "type": "module", so a plain `.js` here would be CommonJS to Node
 * and ESM to webpack — the one situation that would let the two drift apart.)
 *
 * Why these numbers:
 *   16 kHz   — sung f0 lives at 80–800 Hz; 16 k is ample and a third of the work of 48 k.
 *   1024 tap — 64 ms, ≥2 periods even at 70 Hz, which YIN needs to lock on.
 *   512 hop  — 32 ms. Human timing tolerance is ~50 ms+, so finer buys nothing and
 *              doubles the payload the client has to download per song.
 */
import Pitchfinder from 'pitchfinder';

export const TRACK = Object.freeze({
  sampleRate: 16000,
  frame: 1024,
  hop: 512,
  /** YIN threshold. 0.1 (the library default) rejected ~60% of genuinely sung frames
   *  on our two songs; 0.2 recovered them while the instrumental-only intro of
   *  Tiranga still measured 0.2% voiced, so it is not simply letting noise through. */
  threshold: 0.2,
  fMin: 70,
  fMax: 900,
  /** Vocal band. Stripping bass and cymbals is what took the residual from unusable
   *  to a 148× voiced-rate separation against a known instrumental-only passage. */
  bandLo: 90,
  bandHi: 1000,
  /** Absolute energy floor. Do NOT remove in favour of a purely adaptive gate: a
   *  muted mic would then have its own noise floor normalised up into "singing",
   *  which is exactly the silent-mic gaming case the feature has to refuse. */
  energyFloor: 0.0025,
});

/**
 * Resample to exactly 16 kHz.
 *
 * The ratio must NOT be rounded to an integer. 48 kHz divides by 3 cleanly, but a
 * browser AudioContext very often runs at 44.1 kHz, where rounding 2.756 to 3 yields
 * a 14.7 kHz stream that everything downstream believes is 16 kHz. Every detected
 * pitch would come out ~1.5 semitones flat — a systematic error that would look like
 * "everyone sings flat" rather than like a bug.
 *
 * Box-average over the source window (anti-aliasing) plus linear interpolation at
 * the edges. The band of interest is far below Nyquist either way, so this is ample.
 */
export function downsampleTo16k(input, inputRate) {
  const ratio = inputRate / TRACK.sampleRate;
  if (Math.abs(ratio - 1) < 1e-9) return Float32Array.from(input);
  if (ratio < 1) throw new Error(`cannot upsample from ${inputRate} Hz`);
  const out = new Float32Array(Math.floor(input.length / ratio));
  for (let i = 0; i < out.length; i += 1) {
    const start = i * ratio;
    const end = start + ratio;
    const first = Math.floor(start);
    const last = Math.min(input.length - 1, Math.floor(end));
    let sum = 0;
    let weight = 0;
    for (let k = first; k <= last; k += 1) {
      // Partial weight for the samples straddling each end of the window.
      const w = Math.min(end, k + 1) - Math.max(start, k);
      if (w <= 0) continue;
      sum += (input[k] || 0) * w;
      weight += w;
    }
    out[i] = weight > 0 ? sum / weight : 0;
  }
  return out;
}

/**
 * One-pole highpass then one-pole lowpass, with the filter state kept outside the
 * call.
 *
 * The state matters for live capture: the mic arrives in 4096-sample blocks, and a
 * filter that resets per block puts a transient at every boundary — 12 discontinuities
 * a second for YIN to trip over. The offline path passes the whole signal at once and
 * never notices, which is exactly how that bug would have survived to production.
 */
export function createBandpass(sampleRate, lo = TRACK.bandLo, hi = TRACK.bandHi) {
  const a = Math.exp((-2 * Math.PI * lo) / sampleRate);
  const b = Math.exp((-2 * Math.PI * hi) / sampleRate);
  let hp = 0;
  let prev = 0;
  let lp = 0;
  return function process(input) {
    const out = new Float32Array(input.length);
    for (let i = 0; i < input.length; i += 1) {
      hp = a * (hp + input[i] - prev);
      prev = input[i];
      lp += (1 - b) * (hp - lp);
      out[i] = lp;
    }
    return out;
  };
}

/** Whole-signal convenience wrapper. */
export function bandpass(input, sampleRate, lo = TRACK.bandLo, hi = TRACK.bandHi) {
  return createBandpass(sampleRate, lo, hi)(input);
}

/** YIN configured identically everywhere. Never construct one inline. */
export function createDetector() {
  return Pitchfinder.YIN({ sampleRate: TRACK.sampleRate, threshold: TRACK.threshold });
}

/**
 * Apply the adaptive gate to frames captured live.
 *
 * Live capture cannot know the take's 90th-percentile energy until the take is over,
 * so it runs YIN against the absolute floor and keeps everything. Since the adaptive
 * gate is always >= that floor, the captured set is a superset of the gated set and
 * this only ever removes frames — the result is identical to `trackFrames` having
 * seen the whole signal up front.
 */
export function applyAdaptiveGate(frames) {
  const gate = Math.max(
    TRACK.energyFloor,
    0.1 * percentile(frames.map((f) => f.energy), 0.9),
  );
  return correctOctaves(frames.map((f) => (f.energy > gate ? f : { ...f, midi: null })));
}

export function hzToMidi(hz) {
  return 69 + 12 * Math.log2(hz / 440);
}

export function midiToHz(midi) {
  return 440 * 2 ** ((midi - 69) / 12);
}

function rms(frame) {
  let sum = 0;
  for (let i = 0; i < frame.length; i += 1) sum += frame[i] * frame[i];
  return Math.sqrt(sum / frame.length);
}

/** RMS of one analysis frame. Shared so the live meter and the final score agree on
 *  what "loud enough to be singing" means. */
export const frameEnergy = rms;

function percentile(values, p) {
  if (!values.length) return 0;
  const sorted = Float64Array.from(values).sort();
  return sorted[Math.min(sorted.length - 1, Math.floor(sorted.length * p))];
}

/**
 * YIN reports a clean 2× (or ½×) of the true f0 often enough that a raw contour has
 * visible octave jumps — Jai Hanuman's median came out at 390 Hz with the 90th
 * percentile at exactly double that. Snap each voiced frame to whichever octave sits
 * closest to the local median, so a run of notes stays on one ladder.
 *
 * This runs on the reference and on the singer, so a singer an octave down (a man
 * covering a woman's line, which is most of this site) is not punished for it.
 */
export function correctOctaves(frames, windowFrames = 31) {
  const midis = frames.map((f) => f.midi);
  const out = frames.map((f) => ({ ...f }));
  const half = Math.floor(windowFrames / 2);
  for (let i = 0; i < out.length; i += 1) {
    if (out[i].midi == null) continue;
    const local = [];
    for (let k = Math.max(0, i - half); k < Math.min(out.length, i + half + 1); k += 1) {
      if (midis[k] != null) local.push(midis[k]);
    }
    if (local.length < 5) continue;
    const median = percentile(local, 0.5);
    let best = out[i].midi;
    let bestDist = Math.abs(best - median);
    for (const shift of [-24, -12, 12, 24]) {
      const cand = out[i].midi + shift;
      const dist = Math.abs(cand - median);
      if (dist < bestDist) { bestDist = dist; best = cand; }
    }
    out[i].midi = best;
  }
  return out;
}

/**
 * Frame a 16 kHz mono signal into { t, midi, energy }.
 *
 * `midi` is null for unvoiced/silent frames — that is a real signal, not missing
 * data, and the scorer depends on being able to tell "sang the wrong note" from
 * "did not sing at all".
 *
 * The gate is the larger of an absolute floor and a fraction of the take's own 90th
 * percentile energy, so a quiet mic still scores while a muted one cannot.
 */
export function trackFrames(signal16k, { adaptiveGate = true } = {}) {
  const detect = Pitchfinder.YIN({
    sampleRate: TRACK.sampleRate,
    threshold: TRACK.threshold,
  });
  const { frame, hop, sampleRate, fMin, fMax, energyFloor } = TRACK;

  const energies = [];
  for (let i = 0; i + frame <= signal16k.length; i += hop) {
    energies.push(rms(signal16k.subarray(i, i + frame)));
  }
  const gate = adaptiveGate
    ? Math.max(energyFloor, 0.1 * percentile(energies, 0.9))
    : energyFloor;

  const frames = [];
  let idx = 0;
  for (let i = 0; i + frame <= signal16k.length; i += hop, idx += 1) {
    const energy = energies[idx];
    let midi = null;
    if (energy > gate) {
      const hz = detect(signal16k.subarray(i, i + frame));
      if (hz && hz > fMin && hz < fMax) midi = hzToMidi(hz);
    }
    frames.push({ t: i / sampleRate, midi, energy });
  }
  return correctOctaves(frames);
}

/** Fraction of frames that carry a pitch. The single most useful sanity number:
 *  a real take sits around 0.3–0.6, silence sits at 0. */
export function voicedRatio(frames) {
  if (!frames.length) return 0;
  return frames.filter((f) => f.midi != null).length / frames.length;
}
